import { useState } from 'react';
import { Alert, Button, Card, Icon, Table, type Column } from '@evogard/ui';
import type { ManagementTypes } from '@evogard/contract';
import type { ClaimFlowPeriod } from '@evogard/domain';
import { CLAIM_TYPE_OPTIONS } from '../claim/labels.js';
import { DonutFigure, donutToneAt } from './Donut.js';
import { useManagementTypes } from './useManagement.js';

/**
 * Os tipos de sinistro (26/10/2026): a pizza do Painel da Gestão do sinistro, pelo tipo do
 * evento que o aviso registrou.
 *
 * Rosca, com o total no meio: a fatia diz a proporção, e a lista ao lado dá o
 * número exato e a ordem, que o ângulo sozinho não dá. A cor é **do tipo**, na
 * ordem do formulário do aviso, e não da posição na lista — trocar o período
 * reordena a lista sem trocar a cor de Colisão.
 */

/* A cor do tipo, pela ordem do formulário do aviso; fora dele (dado antigo), cinza. */
const toneOf = (type: string) =>
  donutToneAt(CLAIM_TYPE_OPTIONS.findIndex((o) => o.value === type));
const labelOf = (type: string) =>
  CLAIM_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

export function ClaimTypesChart({ days }: { days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const query = useManagementTypes(days);
  const data = query.data;

  return (
    <Card
      as="figure"
      className="@container"
      title="Tipos de sinistro"
      descricao="O evento que o aviso registrou, só o sinistro do associado."
      actions={
        !!data?.total &&
        !query.isError && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver gráfico' : 'Ver tabela'}
          </Button>
        )
      }
    >
      {query.isError ? (
        <Alert tone="erro" title="Não conseguimos carregar os tipos de sinistro">
          Nada foi desenhado, para não mostrar número velho como se fosse atual.
          <span className="mt-1 block font-mono text-xs">{query.error.message}</span>
        </Alert>
      ) : !data ? (
        <div
          className="h-64 animate-pulse rounded-borda bg-superficie-suave"
          aria-busy="true"
        />
      ) : data.total === 0 ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum sinistro de associado aberto nos últimos {days} dias. Quando houver, o
          gráfico mostra de que tipo cada um foi.
        </p>
      ) : asTable ? (
        <TypesTable data={data} />
      ) : (
        <TypesFigure data={data} />
      )}
    </Card>
  );
}

function TypesFigure({ data }: { data: ManagementTypes }) {
  return (
    <DonutFigure
      label="Sinistros por tipo"
      noun={['sinistro', 'sinistros']}
      slices={data.types.map((t) => ({
        key: t.type,
        label: labelOf(t.type),
        total: t.total,
        tone: toneOf(t.type),
      }))}
    />
  );
}

type TypeRow = ManagementTypes['types'][number];

function TypesTable({ data }: { data: ManagementTypes }) {
  const columns: Column<TypeRow>[] = [
    { title: 'Tipo', cell: (r) => labelOf(r.type), alignment: 'inicio' },
    { title: 'Sinistros', cell: (r) => r.total, alignment: 'fim' },
    { title: '%', cell: (r) => `${share(r.total, data.total)}%`, alignment: 'fim' },
  ];
  return (
    <Table
      columns={columns}
      items={data.types}
      rowKey={(r) => r.type}
      label={`Sinistros por tipo nos últimos ${data.days} dias`}
      empty="Nenhum sinistro no período."
    />
  );
}
